import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { fmtINR, fmtDay } from "@/lib/service";
import { TierChip } from "@/components/TargetPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WarrantyToggle, basisText, useExWarranty } from "@/components/WarrantyToggle";
import { toast } from "sonner";
import { Trophy, Medal, AlertTriangle, Flame, Target, Warehouse } from "lucide-react";
import { fmtBay } from "@/components/service-overview/SvcBits";

const MEDAL = { 1: "bg-amber-400 text-amber-950", 2: "bg-slate-300 text-slate-900", 3: "bg-orange-300 text-orange-950" };
const Place = ({ rank, red }) => <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold ${red ? "bg-red-600 text-white" : MEDAL[rank] || "bg-secondary text-foreground"}`}>{rank}</span>;
const isPct = (b) => b.kind === "pct";
const isBay = (b) => b.kind === "bay";
const valueOf = (b, r) => (isPct(b) ? `${Math.round(r.pct)}%` : isBay(b) ? fmtBay(r.per_bay_day) : `₹${fmtINR(r.per_ro)}`);
const scale = (b, r) => (isPct(b) ? Math.min(r.pct, 120) / 120 : isBay(b) ? (b.best ? r.per_bay_day / b.best : 0) : b.best ? r.per_ro / b.best : 0);
const subOf = (b, r, n) => (isBay(b) ? `${r.ro} RO · ${r.bays} bays · ${r.active_days} d${n ? ` · ${r.rank}/${n}` : ""}` : `${r.ro} RO${n ? ` · ${r.rank}/${n}` : ""}`);

function Row({ b, r, n, own, red }) {
  const width = Math.max(4, Math.round(scale(b, r) * 100));
  const name = <>{r.code} <span className="text-muted-foreground font-normal">{r.name}</span></>;
  return (
    <li className={`flex items-center gap-2.5 px-3 py-1.5 ${r.mine ? (red ? "bg-red-100" : "bg-amber-50/70") : red ? "bg-red-50/60" : ""}`} data-testid={`lb-row-${b.key}-${r.code}`}>
      <Place rank={r.rank} red={red} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className={`truncate font-semibold ${red ? "text-red-900" : ""}`}>{own ? <Link to={`/service/workshop/${r.code}`} className="hover:underline">{name}</Link> : name}{r.mine && <span className={`ml-1.5 rounded px-1 text-[9px] font-bold uppercase tracking-wider ${red ? "bg-red-600 text-white" : "bg-amber-200 text-amber-900"}`}>you</span>}</span>
          <span className={`num font-bold whitespace-nowrap ${red ? "text-red-700" : ""}`}>{valueOf(b, r)}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="h-1 flex-1 rounded-full bg-secondary overflow-hidden"><div className={`h-full rounded-full ${red ? "bg-red-500" : "bg-foreground"}`} style={{ width: `${width}%` }} /></div>
          <span className="num text-[10px] text-muted-foreground whitespace-nowrap flex items-center gap-1">{isPct(b) ? <>{r.tier && <TierChip tier={r.tier} />}{r.streak > 0 && <span className="flex items-center text-orange-600"><Flame size={10} />{r.streak}</span>}{n ? <span>{r.rank}/{n}</span> : null}</> : subOf(b, r, n)}</span>
        </div>
      </div>
    </li>
  );
}

function Board({ b, n, myCodes }) {
  const eligible = b.eligible;
  return (
    <div className="border bg-card rounded-md flex flex-col" data-testid={`lb-board-${b.key}`}>
      <div className="px-3 py-2.5 border-b">
        <h3 className="font-heading text-sm font-semibold leading-tight flex items-center gap-1">{isPct(b) && <Target size={13} />}{isBay(b) && <Warehouse size={13} />}{b.label}</h3>
        <p className="text-[10px] text-muted-foreground mt-0.5">{isPct(b) ? `${b.unit} · day ${b.elapsed}/${b.working_days} · ${eligible} workshops with targets` : isBay(b) ? `${b.unit} · group ${fmtBay(b.group_per_bay_day)} · ${eligible} of ${n} workshops with bays${b.unset?.length ? ` · ${b.unset.length} without bays` : ""}` : `${b.unit} · group ₹${fmtINR(b.group_per_ro)} · ${eligible} of ${n} workshops with ROs`}</p>
      </div>
      <ol className="divide-y">
        {b.top.map((r) => <Row key={r.code} b={b} r={r} own={myCodes.includes(r.code)} />)}
        {b.top.length === 0 && <li className="px-3 py-6 text-center text-xs text-muted-foreground">{isBay(b) ? "No workshop has bays captured yet — set GS / BP bays in Admin › Dealers" : "No data in this period"}</li>}
      </ol>
      {b.mine.length > 0 && <div className="border-t border-dashed" data-testid={`lb-mine-${b.key}`}><ol>{b.mine.map((r) => <Row key={r.code} b={b} r={r} n={eligible} own />)}</ol></div>}
      {b.bottom.length > 0 && (
        <div className="mt-auto border-t-2 border-red-200" data-testid={`lb-bottom-${b.key}`}>
          <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-red-700 bg-red-50 flex items-center gap-1"><AlertTriangle size={11} /> Needs a push · bottom {b.bottom.length}</div>
          <ol className="divide-y divide-red-100">{b.bottom.map((r) => <Row key={r.code} b={b} r={r} n={eligible} own={myCodes.includes(r.code)} red />)}</ol>
        </div>
      )}
    </div>
  );
}

function Podium({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="lb-podium">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mr-1 flex items-center gap-1"><Medal size={13} /> Podium finishes</span>
      {rows.map((p) => (
        <span key={p.code} className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[11px] ${p.mine ? "border-amber-400 bg-amber-50" : "bg-card"}`} data-testid={`lb-podium-${p.code}`}>
          <b>{p.code}</b> <span className="text-muted-foreground">{p.name}</span>
          {p.firsts > 0 && <span className="rounded bg-amber-400 px-1 text-[10px] font-extrabold text-amber-950">{p.firsts}× #1</span>}
          <span className="num text-muted-foreground">top-5 in {p.top}</span>
          {p.bottom > 0 && <span className="num text-red-700">· bottom-5 in {p.bottom}</span>}
        </span>
      ))}
    </div>
  );
}

function BottomBanner({ data }) {
  const entries = Object.entries(data.my_bottom_boards || {});
  if (!entries.length) return null;
  const total = data.boards.filter((b) => !isPct(b)).length;
  return (
    <div className="rounded-md border-2 border-red-500 bg-red-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="lb-bottom-banner">
      <AlertTriangle className="text-red-600 shrink-0" size={22} />
      <div className="text-sm">
        <div className="font-extrabold text-red-800">{entries.map(([c, n]) => `${c} is in the bottom 5 on ${n} of ${total} metrics`).join(" · ")}</div>
        <div className="text-xs text-red-700">Getting off this list is the first goal — the boards below show exactly which per-RO numbers need a push.</div>
      </div>
    </div>
  );
}

export default function ServiceLeaderboard() {
  const [month, setMonth] = useState(monthOptions()[1]);
  const [cat, setCat] = useState("GS");
  const [ex, setEx] = useExWarranty();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/service/leaderboard?${qs({ month, cat, ex_warranty: ex })}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e), { id: "lb-error" }));
  }, [month, cat, ex]);

  return (
    <div className="space-y-5" data-testid="service-leaderboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2"><Trophy size={22} /> Leaderboard</h1>
          <p className="text-sm text-muted-foreground">{data ? <>Top {data.top_n} and bottom {data.bottom_n} workshops on every per-RO metric · {data.cat_label} · {fmtDay(data.from)} → {fmtDay(data.to)} · all {data.workshops} workshops · {basisText(data.ex_warranty)}</> : "Top and bottom workshops on every per-RO metric"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WarrantyToggle ex={ex} onChange={setEx} />
          <div className="flex items-center rounded-md border overflow-hidden text-xs" data-testid="lb-cats">
            {(data?.categories || [{ key: "GS", label: "" }]).map((c) => <button key={c.key} onClick={() => setCat(c.key)} className={`px-3 py-1.5 font-semibold transition-colors ${cat === c.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`lb-cat-${c.key}`} title={c.label}>{c.key}</button>)}
          </div>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="lb-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <>
          <BottomBanner data={data} />
          <Podium rows={data.podium} />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 [&>*]:min-w-0">
            {data.boards.map((b) => <Board key={b.key} b={b} n={data.workshops} myCodes={data.my_codes} />)}
          </div>
          <p className="text-[11px] text-muted-foreground">Per-RO = revenue ÷ distinct ROs invoiced in the period · {data.ex_warranty ? "ex-warranty: GST billing only" : "GS labour / parts / revenue include warranty (warranty adds no ROs)"} · workshops without ROs in the category are not ranked · the red "needs a push" zone is the bottom {data.bottom_n} of the ranked workshops · your own workshop is highlighted wherever it stands.</p>
        </>
      )}
    </div>
  );
}
